import { type SetStateAction, useCallback, useState } from 'react';

import type { LensCard, LensCardConfig, LensCardType, LensSortConfig } from '~/types/leLens';

import type { DetailsTabDraft } from './tabs/DetailsTab';

export type CardEditorDraft = {
  data: { cardId: string; datasetId?: string; view: LensCard['view']; period?: LensCard['period'] } | null;
  type: LensCardType | null;
  details: DetailsTabDraft | null;
  config: LensCardConfig | null;
  sort: LensSortConfig | undefined;
};

/** Drafts belong to a Lens/card, not the lifetime of its open panel. */
export function useCardEditorDrafts(lensId: string | undefined, cardId: string | null, savedSort?: LensSortConfig) {
  const [allDrafts, setAllDrafts] = useState<Record<string, Record<string, CardEditorDraft>>>({});
  const drafts = allDrafts[lensId ?? ''] ?? {};
  const draft = cardId ? drafts[cardId] : undefined;
  const setField = useCallback(
    <K extends keyof CardEditorDraft>(field: K, action: SetStateAction<CardEditorDraft[K]>) => {
      if (!lensId || !cardId) return;
      setAllDrafts((current) => {
        const cards = current[lensId] ?? {};
        const previous = cards[cardId] ?? { data: null, type: null, details: null, config: null, sort: savedSort };
        const value =
          typeof action === 'function'
            ? (action as (value: CardEditorDraft[K]) => CardEditorDraft[K])(previous[field])
            : action;
        return { ...current, [lensId]: { ...cards, [cardId]: { ...previous, [field]: value } } };
      });
    },
    [lensId, cardId, savedSort],
  );
  const clear = useCallback(() => {
    if (!lensId || !cardId) return;
    setAllDrafts((current) => {
      const cards = { ...current[lensId] };
      delete cards[cardId];
      return { ...current, [lensId]: cards };
    });
  }, [lensId, cardId]);
  return { drafts, draft, setField, clear };
}
